import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Folder,
  FileText,
  Loader2,
  Search,
  FlaskConical,
  Hash,
  Upload,
  Download,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  RotateCcw,
  TrendingDown,
  DollarSign,
  Tag,
  X,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminCatalogo() {
  const [grupos, setGrupos] = useState([]);
  const [pruebas, setPruebas] = useState([]);
  const [aplicacionesClinicas, setAplicacionesClinicas] = useState([]);
  const [especies, setEspecies] = useState([]);
  const [areasDiagnosticas, setAreasDiagnosticas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showGrupoDialog, setShowGrupoDialog] = useState(false);
  const [showPruebaDialog, setShowPruebaDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showAppClinicaDialog, setShowAppClinicaDialog] = useState(false);
  const [editingGrupo, setEditingGrupo] = useState(null);
  const [editingPrueba, setEditingPrueba] = useState(null);
  const [editingAppClinica, setEditingAppClinica] = useState(null);
  const [selectedGrupo, setSelectedGrupo] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactivos, setShowInactivos] = useState('activos'); // 'activos' | 'inactivos' | 'todos'
  const [showAppClinicaManager, setShowAppClinicaManager] = useState(false);
  const [showEspecieManager, setShowEspecieManager] = useState(false);
  const [showAreaManager, setShowAreaManager] = useState(false);
  const [appClinicaForm, setAppClinicaForm] = useState({ nombre: '', activo: true });
  const [especieForm, setEspecieForm] = useState({ nombre: '', activo: true });
  const [areaForm, setAreaForm] = useState({ nombre: '', orden: 0, activo: true });
  const [editingEspecie, setEditingEspecie] = useState(null);
  const [editingArea, setEditingArea] = useState(null);
  // Per-manager filter dropdowns
  const [appsFilter, setAppsFilter] = useState('activos');
  const [especiesFilter, setEspeciesFilter] = useState('activos');
  const [areasFilter, setAreasFilter] = useState('activos');
  const navigate = useNavigate();
  const fileInputRef = React.useRef(null);

  const [grupoForm, setGrupoForm] = useState({ 
    nombre: '', 
    orden: 0,
    area_diagnostica_id: '',
    usa_precio_escalonado: false,
    precio_base: 0,
    incremento_adicional: 0,
    activo: true
  });
  const [pruebaForm, setPruebaForm] = useState({
    nombre: '',
    codigo: '',
    descripcion: '',
    tipo_muestra: '',
    precio: 0,
    orden: 0,
    grupo_id: '',
    aplicaciones_clinicas: [],
    especies: [],
    activo: true,
  });

  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };
  const [userPermisos, setUserPermisos] = useState(null);

  // Permission helpers
  const canCreate = () => userPermisos?.catalogo?.includes('crear');
  const canEdit = () => userPermisos?.catalogo?.includes('editar');
  const canDelete = () => userPermisos?.catalogo?.includes('eliminar');
  const canImport = () => userPermisos?.catalogo?.includes('importar');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchUserPermisos();
    fetchData();
  }, [token, navigate]);

  const fetchUserPermisos = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/me`, { headers });
      setUserPermisos(response.data.permisos);
    } catch (error) {
      console.error('Error fetching user permissions:', error);
    }
  };

  const fetchData = async () => {
    try {
      // Always fetch ALL records (incluir_inactivos=true) and filter client-side based on showInactivos.
      // This avoids extra round-trips when user toggles the dropdown.
      const params = { incluir_inactivos: true };
      const [gruposRes, pruebasRes, appsRes, especiesRes, areasRes] = await Promise.all([
        axios.get(`${API}/admin/grupos-pruebas`, { headers, params }),
        axios.get(`${API}/admin/pruebas`, { headers, params }),
        axios.get(`${API}/admin/aplicaciones-clinicas`, { headers, params }),
        axios.get(`${API}/admin/especies`, { headers, params }),
        axios.get(`${API}/admin/areas-diagnosticas`, { headers, params }),
      ]);
      
      setGrupos(gruposRes.data);
      setPruebas(pruebasRes.data);
      setAplicacionesClinicas(appsRes.data);
      setEspecies(especiesRes.data);
      setAreasDiagnosticas(areasRes.data);

      const activeGrupos = gruposRes.data.filter((g) => g.activo);
      
      if (activeGrupos.length > 0 && !selectedGrupo) {
        setSelectedGrupo(activeGrupos[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        if (error.response?.status === 403) {
          toast.error('No tiene permiso para ver el catálogo');
        }
        navigate('/admin/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveGrupo = async () => {
    if (!grupoForm.nombre) {
      toast.error('El nombre es requerido');
      return;
    }

    setIsSaving(true);
    try {
      if (editingGrupo) {
        // 1) Update other fields (no activo here — backend update doesn't touch activo)
        const { activo: _activo, ...updatable } = grupoForm;
        await axios.put(`${API}/admin/grupos-pruebas/${editingGrupo.id}`, updatable, { headers });
        // 2) If activo state changed, call dedicated endpoint
        const wasActive = editingGrupo.activo !== false;
        if (wasActive && !grupoForm.activo) {
          if (window.confirm('Al desactivar el grupo se desactivarán también sus pruebas activas. ¿Continuar?')) {
            const res = await axios.delete(`${API}/admin/grupos-pruebas/${editingGrupo.id}`, { headers });
            const cascade = res.data?.pruebas_desactivadas || 0;
            toast.success(cascade > 0 ? `Grupo desactivado (${cascade} prueba${cascade !== 1 ? 's' : ''} en cascada)` : 'Grupo desactivado');
          } else {
            setIsSaving(false);
            return;
          }
        } else if (!wasActive && grupoForm.activo) {
          await axios.patch(`${API}/admin/grupos-pruebas/${editingGrupo.id}/activar`, {}, { headers });
          toast.success('Grupo reactivado');
        } else {
          toast.success('Grupo actualizado');
        }
      } else {
        const { activo: _ignore, ...createPayload } = grupoForm;
        await axios.post(`${API}/admin/grupos-pruebas`, createPayload, { headers });
        toast.success('Grupo creado');
      }
      setShowGrupoDialog(false);
      resetGrupoForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteGrupo = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Está seguro de desactivar este grupo? También se desactivarán sus pruebas activas.')) return;

    try {
      const res = await axios.delete(`${API}/admin/grupos-pruebas/${id}`, { headers });
      const cascade = res.data?.pruebas_desactivadas || 0;
      toast.success(cascade > 0 ? `Grupo desactivado (${cascade} prueba${cascade !== 1 ? 's' : ''} en cascada)` : 'Grupo desactivado');
      if (selectedGrupo?.id === id) {
        setSelectedGrupo(null);
      }
      fetchData();
    } catch (error) {
      toast.error('Error al desactivar');
    }
  };

  const handleReactivarGrupo = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Reactivar este grupo? Sus pruebas seguirán inactivas hasta que las reactive individualmente.')) return;
    try {
      await axios.patch(`${API}/admin/grupos-pruebas/${id}/activar`, {}, { headers });
      toast.success('Grupo reactivado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reactivar');
    }
  };

  const handleReactivarPrueba = async (id) => {
    try {
      await axios.patch(`${API}/admin/pruebas/${id}/activar`, {}, { headers });
      toast.success('Prueba reactivada');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reactivar');
    }
  };

  const handleSavePrueba = async () => {
    if (!pruebaForm.nombre || !pruebaForm.codigo) {
      toast.error('Nombre y código son requeridos');
      return;
    }

    const grupoId = editingPrueba ? pruebaForm.grupo_id : selectedGrupo?.id;
    if (!grupoId) {
      toast.error('Debe seleccionar un grupo');
      return;
    }

    setIsSaving(true);
    try {
      const { activo: _activo, ...rest } = pruebaForm;
      const payload = { ...rest, grupo_id: grupoId };
      if (editingPrueba) {
        await axios.put(`${API}/admin/pruebas/${editingPrueba.id}`, payload, { headers });
        // Handle activo toggle
        const wasActive = editingPrueba.activo !== false;
        if (wasActive && !pruebaForm.activo) {
          if (window.confirm('¿Desactivar esta prueba? Las clínicas no podrán seleccionarla en nuevas solicitudes.')) {
            await axios.delete(`${API}/admin/pruebas/${editingPrueba.id}`, { headers });
            toast.success('Prueba desactivada');
          } else {
            setIsSaving(false);
            return;
          }
        } else if (!wasActive && pruebaForm.activo) {
          try {
            await axios.patch(`${API}/admin/pruebas/${editingPrueba.id}/activar`, {}, { headers });
            toast.success('Prueba reactivada');
          } catch (err) {
            toast.error(err.response?.data?.detail || 'No se pudo reactivar');
            setIsSaving(false);
            return;
          }
        } else {
          toast.success('Prueba actualizada');
        }
      } else {
        await axios.post(`${API}/admin/pruebas`, payload, { headers });
        toast.success('Prueba creada');
      }
      setShowPruebaDialog(false);
      resetPruebaForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePrueba = async (id) => {
    if (!window.confirm('¿Está seguro de desactivar esta prueba?')) return;

    try {
      await axios.delete(`${API}/admin/pruebas/${id}`, { headers });
      toast.success('Prueba desactivada');
      fetchData();
    } catch (error) {
      toast.error('Error al desactivar');
    }
  };

  const resetGrupoForm = () => {
    setGrupoForm({ 
      nombre: '', 
      orden: 0,
      area_diagnostica_id: '',
      usa_precio_escalonado: false,
      precio_base: 0,
      incremento_adicional: 0,
      activo: true
    });
    setEditingGrupo(null);
  };

  const resetPruebaForm = () => {
    setPruebaForm({ nombre: '', codigo: '', descripcion: '', tipo_muestra: '', precio: 0, orden: 0, grupo_id: '', aplicaciones_clinicas: [], especies: [], activo: true });
    setEditingPrueba(null);
  };

  const openEditGrupo = (grupo, e) => {
    e.stopPropagation();
    setEditingGrupo(grupo);
    setGrupoForm({ 
      nombre: grupo.nombre, 
      orden: grupo.orden,
      area_diagnostica_id: grupo.area_diagnostica_id || '',
      usa_precio_escalonado: grupo.usa_precio_escalonado || false,
      precio_base: grupo.precio_base || 0,
      incremento_adicional: grupo.incremento_adicional || 0,
      activo: grupo.activo !== false
    });
    setShowGrupoDialog(true);
  };

  const openAddPrueba = () => {
    if (!selectedGrupo) {
      toast.error('Seleccione un grupo primero');
      return;
    }
    setShowPruebaDialog(true);
  };

  const openEditPrueba = (prueba) => {
    setEditingPrueba(prueba);
    setPruebaForm({
      nombre: prueba.nombre,
      codigo: prueba.codigo,
      descripcion: prueba.descripcion || '',
      tipo_muestra: prueba.tipo_muestra || '',
      precio: prueba.precio || 0,
      orden: prueba.orden,
      grupo_id: prueba.grupo_id,
      aplicaciones_clinicas: prueba.aplicaciones_clinicas || [],
      especies: prueba.especies || [],
      activo: prueba.activo !== false,
    });
    setShowPruebaDialog(true);
  };

  // Filter helper: returns true if item passes the activo/inactivo dropdown filter
  const passActivoFilter = (item) => {
    if (showInactivos === 'activos') return item.activo !== false;
    if (showInactivos === 'inactivos') return item.activo === false;
    return true; // 'todos'
  };

  const getPruebasForGrupo = (grupoId) => {
    let filtered = pruebas.filter((p) => p.grupo_id === grupoId && passActivoFilter(p));
    if (searchTerm) {
      filtered = filtered.filter(
        (p) =>
          p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.codigo.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered.sort((a, b) => (a.orden || 0) - (b.orden || 0));
  };

  // Aplicaciones Clínicas management
  const handleSaveAppClinica = async () => {
    if (!appClinicaForm.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setIsSaving(true);
    try {
      const { activo: _ignore, ...payload } = appClinicaForm;
      if (editingAppClinica) {
        await axios.put(`${API}/admin/aplicaciones-clinicas/${editingAppClinica.id}`, payload, { headers });
        const wasActive = editingAppClinica.activo !== false;
        if (wasActive && !appClinicaForm.activo) {
          await axios.delete(`${API}/admin/aplicaciones-clinicas/${editingAppClinica.id}`, { headers });
          toast.success('Aplicación desactivada');
        } else if (!wasActive && appClinicaForm.activo) {
          await axios.patch(`${API}/admin/aplicaciones-clinicas/${editingAppClinica.id}/activar`, {}, { headers });
          toast.success('Aplicación reactivada');
        } else {
          toast.success('Aplicación clínica actualizada');
        }
      } else {
        await axios.post(`${API}/admin/aplicaciones-clinicas`, payload, { headers });
        toast.success('Aplicación clínica creada');
      }
      setAppClinicaForm({ nombre: '', activo: true });
      setEditingAppClinica(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAppClinica = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta aplicación clínica? Se removerá de todas las pruebas que la tengan asignada.')) return;
    try {
      await axios.delete(`${API}/admin/aplicaciones-clinicas/${id}`, { headers });
      toast.success('Aplicación clínica eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getAppClinicaNombre = (appId) => {
    const app = aplicacionesClinicas.find(a => a.id === appId);
    return app?.nombre || '';
  };

  const toggleAppClinica = (appId) => {
    setPruebaForm(prev => {
      const current = prev.aplicaciones_clinicas || [];
      const updated = current.includes(appId)
        ? current.filter(id => id !== appId)
        : [...current, appId];
      return { ...prev, aplicaciones_clinicas: updated };
    });
  };

  // Especies management
  const handleSaveEspecie = async () => {
    if (!especieForm.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setIsSaving(true);
    try {
      const { activo: _ignore, ...payload } = especieForm;
      if (editingEspecie) {
        await axios.put(`${API}/admin/especies/${editingEspecie.id}`, payload, { headers });
        const wasActive = editingEspecie.activo !== false;
        if (wasActive && !especieForm.activo) {
          await axios.delete(`${API}/admin/especies/${editingEspecie.id}`, { headers });
          toast.success('Especie desactivada');
        } else if (!wasActive && especieForm.activo) {
          await axios.patch(`${API}/admin/especies/${editingEspecie.id}/activar`, {}, { headers });
          toast.success('Especie reactivada');
        } else {
          toast.success('Especie actualizada');
        }
      } else {
        await axios.post(`${API}/admin/especies`, payload, { headers });
        toast.success('Especie creada');
      }
      setEspecieForm({ nombre: '', activo: true });
      setEditingEspecie(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteEspecie = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta especie? Se removerá de todas las pruebas que la tengan asignada.')) return;
    try {
      await axios.delete(`${API}/admin/especies/${id}`, { headers });
      toast.success('Especie eliminada');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const getEspecieNombre = (espId) => {
    const esp = especies.find(e => e.id === espId);
    return esp?.nombre || '';
  };

  const toggleEspecie = (espId) => {
    setPruebaForm(prev => {
      const current = prev.especies || [];
      const updated = current.includes(espId)
        ? current.filter(id => id !== espId)
        : [...current, espId];
      return { ...prev, especies: updated };
    });
  };

  // Áreas Diagnósticas management
  const handleSaveArea = async () => {
    if (!areaForm.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setIsSaving(true);
    try {
      const { activo: _ignore, ...payload } = areaForm;
      if (editingArea) {
        await axios.put(`${API}/admin/areas-diagnosticas/${editingArea.id}`, payload, { headers });
        const wasActive = editingArea.activo !== false;
        if (wasActive && !areaForm.activo) {
          await axios.delete(`${API}/admin/areas-diagnosticas/${editingArea.id}`, { headers });
          toast.success('Área desactivada');
        } else if (!wasActive && areaForm.activo) {
          await axios.patch(`${API}/admin/areas-diagnosticas/${editingArea.id}/activar`, {}, { headers });
          toast.success('Área reactivada');
        } else {
          toast.success('Área diagnóstica actualizada');
        }
      } else {
        await axios.post(`${API}/admin/areas-diagnosticas`, payload, { headers });
        toast.success('Área diagnóstica creada');
      }
      setAreaForm({ nombre: '', orden: 0, activo: true });
      setEditingArea(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteArea = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta área? Los grupos que la tengan asignada perderán esa referencia.')) return;
    try {
      await axios.delete(`${API}/admin/areas-diagnosticas/${id}`, { headers });
      toast.success('Área diagnóstica eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getAreaNombre = (areaId) => {
    const area = areasDiagnosticas.find(a => a.id === areaId);
    return area?.nombre || '';
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.csv')) {
      toast.error('Por favor seleccione un archivo Excel (.xlsx, .xls) o CSV (.csv)');
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/admin/catalogo/importar`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      });

      setImportResult(response.data.resultados);
      toast.success('Importación completada');
      fetchData(); // Reload data
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Error al importar el archivo');
      setImportResult({ error: error.response?.data?.detail || 'Error desconocido' });
    } finally {
      setIsImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExportExcel = async () => {
    try {
      toast.info('Generando archivo Excel...');
      const response = await axios.get(`${API}/admin/catalogo/exportar`, {
        headers,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.download = `catalogo_pruebas_${today}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Catálogo exportado exitosamente');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Error al exportar el catálogo');
    }
  };

  const downloadTemplate = () => {
    // Create a simple CSV template (Excel can open it)
    const csvHeaders = ['Grupo', 'Nombre', 'Código', 'Descripción', 'Tipo de muestra', 'Orden', 'Precio (MXN)', 'Aplicaciones Clínicas', 'Especies'];
    const exampleRows = [
      ['BIOQUÍMICA CLÍNICA', 'Glucosa', 'BQ-001', 'Medición de glucosa en sangre', 'Suero. Refrigerar 2-8°C.', '1', '150.00', 'Sistémico', 'Canino, Felino'],
      ['BIOQUÍMICA CLÍNICA', 'Urea', 'BQ-002', 'Análisis de urea sérica', '', '2', '120.00', 'Sistémico, Gastrointestinal', 'Canino'],
      ['HEMATOLOGÍA', 'Hemograma Completo', 'HEM-001', 'Conteo completo de células', 'Sangre con EDTA. No congelar.', '1', '250.00', '', ''],
    ];

    // Properly escape CSV values (wrap in quotes if contains comma)
    const escapeCsv = (val) => {
      if (val && val.includes(',')) return `"${val}"`;
      return val;
    };

    const csvContent = [csvHeaders, ...exampleRows]
      .map((row) => row.map(escapeCsv).join(','))
      .join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'plantilla_catalogo.csv';
    link.click();
  };

  // Use ALL grupos (filtering by activo only when needed for UI)
  const activeGrupos = grupos.filter(passActivoFilter);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#505BB3] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/admin/dashboard')}
                className="h-10 border-slate-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-slate-900">Catálogo de Pruebas</h1>
                <p className="text-sm text-slate-500">Administra grupos y pruebas de laboratorio</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="file"
                ref={fileInputRef}
                accept=".xlsx,.xls,.csv"
                onChange={handleImportExcel}
                className="hidden"
              />
              {canEdit() && (
                <Button
                  variant="outline"
                  onClick={() => setShowAreaManager(true)}
                  className="h-10 border-slate-300"
                  data-testid="btn-area-manager"
                >
                  <Folder className="w-4 h-4 mr-2" />
                  Áreas Diagnósticas
                </Button>
              )}
              {canEdit() && (
                <Button
                  variant="outline"
                  onClick={() => setShowAppClinicaManager(true)}
                  className="h-10 border-slate-300"
                  data-testid="btn-app-clinica-manager"
                >
                  <Tag className="w-4 h-4 mr-2" />
                  Aplicaciones Clínicas
                </Button>
              )}
              {canEdit() && (
                <Button
                  variant="outline"
                  onClick={() => setShowEspecieManager(true)}
                  className="h-10 border-slate-300"
                  data-testid="btn-especie-manager"
                >
                  <FlaskConical className="w-4 h-4 mr-2" />
                  Especies
                </Button>
              )}
              {canImport() && (
                <Button
                  variant="outline"
                  onClick={() => setShowImportDialog(true)}
                  className="h-10 border-slate-300"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Importar Excel
                </Button>
              )}
              {canImport() && (
                <Button
                  variant="outline"
                  onClick={handleExportExcel}
                  className="h-10 border-slate-300"
                  data-testid="btn-export-excel"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exportar Excel
                </Button>
              )}
              {canCreate() && (
                <Button
                  onClick={() => setShowGrupoDialog(true)}
                  className="h-10 bg-[#505BB3] hover:bg-[#404A9C] text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Grupo
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Master-Detail Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Groups List */}
        <div className="w-80 bg-white border-r border-slate-200 flex flex-col">
          {/* Panel Header */}
          <div className="bg-[#505BB3] px-4 py-3">
            <div className="flex items-center gap-2 text-white">
              <Folder className="w-5 h-5" />
              <span className="font-semibold">Grupos de Pruebas</span>
              <span className="ml-auto text-sm bg-white/20 px-2 py-0.5 rounded-full">
                {activeGrupos.length}
              </span>
            </div>
          </div>

          {/* Show inactive filter dropdown */}
          <div className="px-4 py-2 border-b border-slate-100 bg-slate-50">
            <Select value={showInactivos} onValueChange={setShowInactivos}>
              <SelectTrigger className="h-8 text-xs bg-white" data-testid="filter-activo-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="activos">
                  <span className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Solo activos
                  </span>
                </SelectItem>
                <SelectItem value="inactivos">
                  <span className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                    Solo inactivos
                  </span>
                </SelectItem>
                <SelectItem value="todos">
                  <span className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                    Todos
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 overflow-y-auto">
            {activeGrupos.length === 0 ? (
              <div className="p-6 text-center">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Folder className="w-6 h-6 text-slate-400" />
                </div>
                <p className="text-slate-500 text-sm">No hay grupos. Cree uno para comenzar.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {activeGrupos.map((grupo) => (
                  <div
                    key={grupo.id}
                    onClick={() => setSelectedGrupo(grupo)}
                    className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                      selectedGrupo?.id === grupo.id
                        ? 'bg-[#505BB3]/5 border-l-4 border-[#505BB3]'
                        : ''
                    } ${!grupo.activo ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            selectedGrupo?.id === grupo.id
                              ? 'bg-[#505BB3] text-white'
                              : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          <Folder className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <p
                            className={`font-medium truncate ${
                              selectedGrupo?.id === grupo.id
                                ? 'text-[#505BB3]'
                                : 'text-slate-900'
                            } ${!grupo.activo ? 'line-through' : ''}`}
                          >
                            {grupo.nombre}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {!grupo.activo && (
                              <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-medium uppercase tracking-wide">
                                Inactivo
                              </span>
                            )}
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <FlaskConical className="w-3 h-3" />
                              {getPruebasForGrupo(grupo.id).length} pruebas
                            </p>
                            {grupo.area_diagnostica_id && (
                              <span className="text-[10px] bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded border border-violet-200 truncate max-w-[120px]">
                                {getAreaNombre(grupo.area_diagnostica_id)}
                              </span>
                            )}
                            {grupo.usa_precio_escalonado && (
                              <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                                <TrendingDown className="w-3 h-3" />
                                Escalonado
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0 ml-2">
                        {!grupo.activo && (
                          <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-medium uppercase tracking-wide self-center">
                            Inactivo
                          </span>
                        )}
                        {canEdit() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-slate-200"
                            onClick={(e) => openEditGrupo(grupo, e)}
                            title="Editar"
                          >
                            <Edit className="w-4 h-4 text-slate-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Tests List */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {selectedGrupo ? (
            <>
              {/* Group Detail Header */}
              <div className="bg-white border-b border-slate-200 p-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-[#505BB3]/10 rounded-xl flex items-center justify-center">
                      <Folder className="w-7 h-7 text-[#505BB3]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">{selectedGrupo.nombre}</h2>
                      <p className="text-sm text-slate-500 mt-1">
                        {getPruebasForGrupo(selectedGrupo.id).length} pruebas en este grupo
                      </p>
                    </div>
                  </div>
                  {canCreate() && (
                    <Button
                      onClick={openAddPrueba}
                      className="bg-[#505BB3] hover:bg-[#404A9C] text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Nueva Prueba
                    </Button>
                  )}
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre o código..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 h-10 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                  />
                </div>
              </div>

              {/* Tests Table */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  {/* Table Header */}
                  <div className="bg-[#505BB3] px-4 py-3">
                    <div className="flex items-center gap-2 text-white">
                      <FlaskConical className="w-5 h-5" />
                      <span className="font-semibold">Pruebas</span>
                      <span className="ml-auto text-sm bg-white/20 px-2 py-0.5 rounded-full">
                        {getPruebasForGrupo(selectedGrupo.id).length}
                      </span>
                    </div>
                  </div>

                  {getPruebasForGrupo(selectedGrupo.id).length === 0 ? (
                    <div className="p-8 text-center">
                      <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <FlaskConical className="w-6 h-6 text-slate-400" />
                      </div>
                      <p className="text-slate-500">
                        {searchTerm ? 'No se encontraron pruebas' : 'No hay pruebas en este grupo'}
                      </p>
                      {!searchTerm && (
                        <Button
                          onClick={openAddPrueba}
                          variant="outline"
                          className="mt-4 border-[#505BB3] text-[#505BB3] hover:bg-[#505BB3] hover:text-white"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Agregar Prueba
                        </Button>
                      )}
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                            Código
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                            Nombre
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase w-24">
                            Precio
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase hidden md:table-cell">
                            Descripción
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-20">
                            Orden
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase w-24">
                            Acciones
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {getPruebasForGrupo(selectedGrupo.id).map((prueba) => (
                          <tr key={prueba.id} className={`hover:bg-slate-50 transition-colors ${!prueba.activo ? 'opacity-60' : ''}`}>
                            <td className="px-4 py-3">
                              <span className="font-mono text-sm bg-slate-100 text-slate-700 px-2 py-1 rounded flex items-center gap-1 w-fit">
                                <Hash className="w-3 h-3" />
                                {prueba.codigo}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-[#505BB3] flex-shrink-0" />
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className={`font-medium text-slate-900 ${!prueba.activo ? 'line-through' : ''}`}>{prueba.nombre}</span>
                                    {!prueba.activo && (
                                      <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-medium uppercase tracking-wide">
                                        Inactiva
                                      </span>
                                    )}
                                  </div>
                                  {((prueba.aplicaciones_clinicas && prueba.aplicaciones_clinicas.length > 0) || (prueba.especies && prueba.especies.length > 0)) && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {(prueba.aplicaciones_clinicas || []).map(appId => {
                                        const nombre = getAppClinicaNombre(appId);
                                        return nombre ? (
                                          <Badge key={appId} variant="secondary" className="text-[10px] px-1.5 py-0 bg-indigo-50 text-indigo-700 border-indigo-200">
                                            {nombre}
                                          </Badge>
                                        ) : null;
                                      })}
                                      {(prueba.especies || []).map(espId => {
                                        const nombre = getEspecieNombre(espId);
                                        return nombre ? (
                                          <Badge key={espId} variant="secondary" className="text-[10px] px-1.5 py-0 bg-emerald-50 text-emerald-700 border-emerald-200">
                                            {nombre}
                                          </Badge>
                                        ) : null;
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="font-semibold text-green-600">
                                ${(prueba.precio || 0).toFixed(2)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500 hidden md:table-cell">
                              {prueba.descripcion || (
                                <span className="text-slate-400 italic">Sin descripción</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600">{prueba.orden}</td>
                            <td className="px-4 py-3">
                              <div className="flex justify-end gap-1">
                                {!prueba.activo && (
                                  <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-medium uppercase tracking-wide self-center mr-1">
                                    Inactiva
                                  </span>
                                )}
                                {canEdit() && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 hover:bg-slate-200"
                                  onClick={() => openEditPrueba(prueba)}
                                  title="Editar"
                                >
                                  <Edit className="w-4 h-4 text-slate-500" />
                                </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Folder className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-slate-500">Seleccione un grupo para ver sus pruebas</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Grupo Dialog */}
      <Dialog
        open={showGrupoDialog}
        onOpenChange={(open) => {
          if (!open) resetGrupoForm();
          setShowGrupoDialog(open);
        }}
      >
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">
              {editingGrupo ? 'Editar Grupo' : 'Nuevo Grupo de Pruebas'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {editingGrupo && (
              <div className={`flex items-center justify-between p-3 rounded-lg border ${grupoForm.activo ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-100 border-slate-300'}`}>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-slate-900">
                    Estado: {grupoForm.activo ? 'Activo' : 'Inactivo'}
                  </span>
                  <span className="text-[11px] text-slate-500">
                    {grupoForm.activo
                      ? 'Visible en catálogo y disponible para solicitudes.'
                      : 'Oculto. Las clínicas no podrán solicitarlo.'}
                  </span>
                </div>
                <Switch
                  checked={grupoForm.activo}
                  onCheckedChange={(checked) => setGrupoForm({ ...grupoForm, activo: checked })}
                  className="data-[state=checked]:bg-emerald-600"
                  data-testid="grupo-activo-toggle"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Nombre *</Label>
              <Input
                value={grupoForm.nombre}
                onChange={(e) => setGrupoForm({ ...grupoForm, nombre: e.target.value })}
                placeholder="Ej: BIOQUÍMICA CLÍNICA"
                className="h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Orden</Label>
              <Input
                type="number"
                value={grupoForm.orden}
                onChange={(e) => setGrupoForm({ ...grupoForm, orden: parseInt(e.target.value) || 0 })}
                placeholder="0"
                className="h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Área Diagnóstica</Label>
              <Select
                value={grupoForm.area_diagnostica_id || 'none'}
                onValueChange={(value) => setGrupoForm({ ...grupoForm, area_diagnostica_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger className="h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]">
                  <SelectValue placeholder="Seleccione un área" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin área asignada</SelectItem>
                  {areasDiagnosticas.map(area => (
                    <SelectItem key={area.id} value={area.id}>{area.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Precio Escalonado Section */}
            <div className="border border-slate-200 rounded-lg p-4 space-y-4 bg-slate-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <TrendingDown className="w-5 h-5 text-green-600" />
                  <div>
                    <Label className="text-sm font-medium text-slate-900">
                      Precio Escalonado por Volumen
                    </Label>
                    <p className="text-xs text-slate-500">
                      Descuento automático por cantidad de pruebas
                    </p>
                  </div>
                </div>
                <Switch
                  checked={grupoForm.usa_precio_escalonado}
                  onCheckedChange={(checked) => setGrupoForm({ ...grupoForm, usa_precio_escalonado: checked })}
                />
              </div>
              
              {grupoForm.usa_precio_escalonado && (
                <div className="space-y-4 pt-2 border-t border-slate-200">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700">
                        Precio Base (1ra prueba)
                      </Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          type="number"
                          step="0.01"
                          value={grupoForm.precio_base}
                          onChange={(e) => setGrupoForm({ ...grupoForm, precio_base: parseFloat(e.target.value) || 0 })}
                          placeholder="950.00"
                          className="h-11 pl-9 border-slate-300 focus:border-green-500 focus:ring-green-500"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700">
                        Incremento Adicional
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">+$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={grupoForm.incremento_adicional}
                          onChange={(e) => setGrupoForm({ ...grupoForm, incremento_adicional: parseFloat(e.target.value) || 0 })}
                          placeholder="150.00"
                          className="h-11 pl-9 border-slate-300 focus:border-green-500 focus:ring-green-500"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Preview */}
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <p className="text-xs font-medium text-green-800 mb-2">Vista Previa:</p>
                    <div className="space-y-1 text-sm text-slate-600">
                      <p>• 1 prueba = <span className="font-semibold text-green-700">${grupoForm.precio_base.toFixed(2)}</span></p>
                      <p>• 2 pruebas = <span className="font-semibold text-green-700">${(grupoForm.precio_base + grupoForm.incremento_adicional).toFixed(2)}</span></p>
                      <p>• 3 pruebas = <span className="font-semibold text-green-700">${(grupoForm.precio_base + grupoForm.incremento_adicional * 2).toFixed(2)}</span></p>
                      <p className="text-xs text-slate-500 mt-2">
                        Fórmula: ${grupoForm.precio_base.toFixed(2)} + (n-1) × ${grupoForm.incremento_adicional.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                resetGrupoForm();
                setShowGrupoDialog(false);
              }}
              className="border-slate-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveGrupo}
              disabled={isSaving}
              className="bg-[#505BB3] hover:bg-[#404A9C] text-white"
            >
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingGrupo ? 'Guardar Cambios' : 'Crear Grupo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prueba Dialog */}
      <Dialog
        open={showPruebaDialog}
        onOpenChange={(open) => {
          if (!open) resetPruebaForm();
          setShowPruebaDialog(open);
        }}
      >
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">
              {editingPrueba ? 'Editar Prueba' : 'Nueva Prueba'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {editingPrueba && (
              <div className={`flex items-center justify-between p-3 rounded-lg border mb-4 ${pruebaForm.activo ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-100 border-slate-300'}`}>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-slate-900">
                    Estado: {pruebaForm.activo ? 'Activa' : 'Inactiva'}
                  </span>
                  <span className="text-[11px] text-slate-500">
                    {pruebaForm.activo
                      ? 'Visible en catálogo y disponible para solicitudes.'
                      : 'Oculta. Las clínicas no podrán seleccionarla.'}
                  </span>
                </div>
                <Switch
                  checked={pruebaForm.activo}
                  onCheckedChange={(checked) => setPruebaForm({ ...pruebaForm, activo: checked })}
                  className="data-[state=checked]:bg-emerald-600"
                  data-testid="prueba-activo-toggle"
                />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              {/* ===================== Columna Izquierda ===================== */}
              <div className="space-y-4">
                {editingPrueba && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Grupo</Label>
                    <Select
                      value={pruebaForm.grupo_id}
                      onValueChange={(value) => setPruebaForm({ ...pruebaForm, grupo_id: value })}
                    >
                      <SelectTrigger className="h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]" data-testid="prueba-grupo-select">
                        <SelectValue placeholder="Seleccione un grupo" />
                      </SelectTrigger>
                      <SelectContent>
                        {grupos
                          .filter((g) => g.activo)
                          .map((g) => (
                            <SelectItem key={g.id} value={g.id}>
                              {g.nombre}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Nombre *</Label>
                  <Input
                    value={pruebaForm.nombre}
                    onChange={(e) => setPruebaForm({ ...pruebaForm, nombre: e.target.value })}
                    placeholder="Ej: Glucosa"
                    className="h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                    data-testid="prueba-nombre-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Código *</Label>
                  <Input
                    value={pruebaForm.codigo}
                    onChange={(e) => setPruebaForm({ ...pruebaForm, codigo: e.target.value })}
                    placeholder="Ej: BQ-001"
                    className="h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                    data-testid="prueba-codigo-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Precio (MXN)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={pruebaForm.precio}
                      onChange={(e) =>
                        setPruebaForm({ ...pruebaForm, precio: parseFloat(e.target.value) || 0 })
                      }
                      placeholder="0.00"
                      className="h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                      data-testid="prueba-precio-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Orden</Label>
                    <Input
                      type="number"
                      value={pruebaForm.orden}
                      onChange={(e) =>
                        setPruebaForm({ ...pruebaForm, orden: parseInt(e.target.value) || 0 })
                      }
                      placeholder="0"
                      className="h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                      data-testid="prueba-orden-input"
                    />
                  </div>
                </div>
                {/* Aplicaciones Clínicas Multi-select */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Aplicaciones Clínicas</Label>
                  {pruebaForm.aplicaciones_clinicas.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {pruebaForm.aplicaciones_clinicas.map(appId => {
                        const nombre = getAppClinicaNombre(appId);
                        return nombre ? (
                          <Badge
                            key={appId}
                            variant="secondary"
                            className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 border-indigo-200 cursor-pointer hover:bg-red-50 hover:text-red-700 hover:border-red-200 transition-colors"
                            onClick={() => toggleAppClinica(appId)}
                            data-testid={`remove-app-${appId}`}
                          >
                            {nombre}
                            <X className="w-3 h-3 ml-1" />
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                  <div className="border border-slate-200 rounded-lg max-h-40 overflow-y-auto p-2">
                    {aplicacionesClinicas.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-2">No hay aplicaciones clínicas configuradas</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-1">
                        {aplicacionesClinicas.map(app => (
                          <label
                            key={app.id}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer transition-colors ${
                              pruebaForm.aplicaciones_clinicas.includes(app.id)
                                ? 'bg-indigo-50 text-indigo-700'
                                : 'hover:bg-slate-50 text-slate-600'
                            }`}
                          >
                            <Checkbox
                              checked={pruebaForm.aplicaciones_clinicas.includes(app.id)}
                              onCheckedChange={() => toggleAppClinica(app.id)}
                              className="data-[state=checked]:bg-[#505BB3] data-[state=checked]:border-[#505BB3]"
                            />
                            <span className="truncate">{app.nombre}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {/* Especies Multi-select */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Especies</Label>
                  {pruebaForm.especies.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {pruebaForm.especies.map(espId => {
                        const nombre = getEspecieNombre(espId);
                        return nombre ? (
                          <Badge
                            key={espId}
                            variant="secondary"
                            className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200 cursor-pointer hover:bg-red-50 hover:text-red-700 hover:border-red-200 transition-colors"
                            onClick={() => toggleEspecie(espId)}
                            data-testid={`remove-esp-${espId}`}
                          >
                            {nombre}
                            <X className="w-3 h-3 ml-1" />
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                  <div className="border border-slate-200 rounded-lg p-2">
                    {especies.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-2">No hay especies configuradas</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-1">
                        {especies.map(esp => (
                          <label
                            key={esp.id}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer transition-colors ${
                              pruebaForm.especies.includes(esp.id)
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'hover:bg-slate-50 text-slate-600'
                            }`}
                          >
                            <Checkbox
                              checked={pruebaForm.especies.includes(esp.id)}
                              onCheckedChange={() => toggleEspecie(esp.id)}
                              className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                            />
                            <span className="truncate">{esp.nombre}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* ===================== Columna Derecha ===================== */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Descripción</Label>
                  <Textarea
                    value={pruebaForm.descripcion}
                    onChange={(e) => setPruebaForm({ ...pruebaForm, descripcion: e.target.value })}
                    placeholder="Descripción opcional de la prueba"
                    rows={8}
                    className="border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3] resize-y"
                    data-testid="prueba-descripcion-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    Tipo de muestra
                    <span className="ml-1 text-xs font-normal text-slate-400">(opcional)</span>
                  </Label>
                  <Textarea
                    value={pruebaForm.tipo_muestra}
                    onChange={(e) => setPruebaForm({ ...pruebaForm, tipo_muestra: e.target.value })}
                    placeholder={'Ej: Refrigerar: Hisopado conjuntival, nasofaríngeo y laríngeo profundo (tonsilas). Secreciones placentarias, vaginales posteriores al aborto, semen, hisopado rectal.'}
                    rows={8}
                    className="border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3] resize-y"
                    data-testid="prueba-tipo-muestra-input"
                  />
                  <p className="text-[11px] text-slate-500">
                    Información sobre los requerimientos de la muestra para realizar el estudio. Se mostrará en el detalle público.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                resetPruebaForm();
                setShowPruebaDialog(false);
              }}
              className="border-slate-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSavePrueba}
              disabled={isSaving}
              className="bg-[#505BB3] hover:bg-[#404A9C] text-white"
            >
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingPrueba ? 'Guardar Cambios' : 'Crear Prueba'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog
        open={showImportDialog}
        onOpenChange={(open) => {
          if (!open) {
            setImportResult(null);
          }
          setShowImportDialog(open);
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">
              Importar Catálogo desde Excel
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {!importResult ? (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h4 className="font-medium text-slate-900 mb-2">Formato requerido:</h4>
                  <p className="text-sm text-slate-600 mb-3">
                    El archivo Excel debe tener las siguientes columnas:
                  </p>
                  <div className="overflow-x-auto">
                    <table className="text-xs w-full border border-slate-300">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="border border-slate-300 px-2 py-1">Grupo</th>
                          <th className="border border-slate-300 px-2 py-1">Nombre</th>
                          <th className="border border-slate-300 px-2 py-1">Código</th>
                          <th className="border border-slate-300 px-2 py-1">Descripción</th>
                          <th className="border border-slate-300 px-2 py-1 bg-amber-100 text-amber-800">Tipo de muestra</th>
                          <th className="border border-slate-300 px-2 py-1">Orden</th>
                          <th className="border border-slate-300 px-2 py-1 bg-green-100 text-green-800">Precio (MXN)</th>
                          <th className="border border-slate-300 px-2 py-1 bg-indigo-100 text-indigo-800">Aplicaciones Clínicas</th>
                          <th className="border border-slate-300 px-2 py-1 bg-emerald-100 text-emerald-800">Especies</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-slate-300 px-2 py-1 text-slate-600">BIOQUÍMICA</td>
                          <td className="border border-slate-300 px-2 py-1 text-slate-600">Glucosa</td>
                          <td className="border border-slate-300 px-2 py-1 text-slate-600">BQ-001</td>
                          <td className="border border-slate-300 px-2 py-1 text-slate-600">Descripción...</td>
                          <td className="border border-slate-300 px-2 py-1 text-amber-700">Suero. Refrigerar.</td>
                          <td className="border border-slate-300 px-2 py-1 text-slate-600">1</td>
                          <td className="border border-slate-300 px-2 py-1 text-green-600 font-medium">150.00</td>
                          <td className="border border-slate-300 px-2 py-1 text-indigo-600">Sistémico</td>
                          <td className="border border-slate-300 px-2 py-1 text-emerald-600">Canino, Felino</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    * Grupo, Nombre y Código son requeridos. Los grupos se crean automáticamente si no existen.
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    * Tipo de muestra: opcional. Texto libre (requerimientos de la muestra). Si la columna está presente y se deja vacía, se borrará el valor existente.
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    * La columna Precio (MXN) es opcional. Si se incluye, actualizará los precios de las pruebas.
                  </p>
                  <p className="text-xs text-indigo-600 mt-1">
                    * Aplicaciones Clínicas: separadas por coma. Vacío = no modifica. "NINGUNA" = borra todas.
                  </p>
                  <p className="text-xs text-emerald-600 mt-1">
                    * Especies: separadas por coma. Vacío = no modifica. "NINGUNA" = borra todas.
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={downloadTemplate}
                    className="flex-1 border-slate-300"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Descargar Plantilla
                  </Button>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isImporting}
                    className="flex-1 bg-[#505BB3] hover:bg-[#404A9C] text-white"
                  >
                    {isImporting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    {isImporting ? 'Importando...' : 'Seleccionar Archivo'}
                  </Button>
                </div>
              </div>
            ) : importResult.error ? (
              <div className="space-y-4">
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <div className="flex items-center gap-2 text-red-700 mb-2">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">Error en la importación</span>
                  </div>
                  <p className="text-sm text-red-600">{importResult.error}</p>
                </div>
                <Button
                  onClick={() => setImportResult(null)}
                  variant="outline"
                  className="w-full"
                >
                  Intentar de nuevo
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center gap-2 text-green-700 mb-3">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">Importación completada</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-white rounded p-3 border border-green-200">
                      <p className="text-2xl font-bold text-green-700">{importResult.grupos_creados}</p>
                      <p className="text-green-600">Grupos creados</p>
                    </div>
                    <div className="bg-white rounded p-3 border border-green-200">
                      <p className="text-2xl font-bold text-blue-700">{importResult.pruebas_creadas}</p>
                      <p className="text-blue-600">Pruebas creadas</p>
                    </div>
                    <div className="bg-white rounded p-3 border border-slate-200">
                      <p className="text-2xl font-bold text-slate-700">{importResult.grupos_existentes}</p>
                      <p className="text-slate-600">Grupos existentes</p>
                    </div>
                    <div className="bg-white rounded p-3 border border-slate-200">
                      <p className="text-2xl font-bold text-slate-700">{importResult.pruebas_actualizadas}</p>
                      <p className="text-slate-600">Pruebas actualizadas</p>
                    </div>
                  </div>
                  {importResult.filas_saltadas && importResult.filas_saltadas.length > 0 && (
                    <div className="mt-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-orange-600" />
                        <p className="text-sm font-semibold text-orange-800">
                          {importResult.filas_saltadas.length} fila{importResult.filas_saltadas.length !== 1 ? 's' : ''} saltada{importResult.filas_saltadas.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="max-h-44 overflow-y-auto space-y-1 pr-1">
                        {importResult.filas_saltadas.map((fs, i) => (
                          <div key={i} className="text-xs bg-white rounded px-2 py-1.5 border border-orange-100 flex items-start gap-2">
                            <span className="font-mono font-semibold text-orange-700 shrink-0">Fila {fs.fila}</span>
                            <div className="min-w-0 flex-1">
                              <p className="text-slate-700 truncate" title={fs.nombre}>{fs.nombre}</p>
                              <p className="text-orange-600 mt-0.5">{fs.motivo}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {importResult.errores && importResult.errores.length > 0 && (
                    <div className="mt-3 p-2 bg-yellow-50 rounded border border-yellow-200">
                      <p className="text-xs font-medium text-yellow-700 mb-1">Advertencias:</p>
                      {importResult.errores.map((err, i) => (
                        <p key={i} className="text-xs text-yellow-600">{err}</p>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  onClick={() => setShowImportDialog(false)}
                  className="w-full bg-[#505BB3] hover:bg-[#404A9C] text-white"
                >
                  Cerrar
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Aplicaciones Clínicas Manager Dialog */}
      <Dialog
        open={showAppClinicaManager}
        onOpenChange={(open) => {
          if (!open) {
            setEditingAppClinica(null);
            setAppClinicaForm({ nombre: '', activo: true });
          }
          setShowAppClinicaManager(open);
        }}
      >
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="app-clinica-manager-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Tag className="w-5 h-5 text-[#505BB3]" />
              Aplicaciones Clínicas
              <span className="text-sm font-normal text-slate-400 ml-auto">
                {aplicacionesClinicas.filter(a => a.activo !== false).length} activas
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-slate-500 mb-4">
              Las aplicaciones clínicas se asignan a cada prueba para clasificarlas (ej: Respiratorio, Sistémico, Vectorial).
            </p>

            {/* Filter dropdown */}
            <div className="mb-3">
              <Select value={appsFilter} onValueChange={setAppsFilter}>
                <SelectTrigger className="h-8 text-xs bg-white" data-testid="apps-clinica-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activos"><span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Solo activas</span></SelectItem>
                  <SelectItem value="inactivos"><span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-slate-400" />Solo inactivas</span></SelectItem>
                  <SelectItem value="todos"><span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-violet-500" />Todas</span></SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Add new / Edit */}
            {canCreate() && (
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Input
                  value={appClinicaForm.nombre}
                  onChange={(e) => setAppClinicaForm({ ...appClinicaForm, nombre: e.target.value })}
                  placeholder={editingAppClinica ? 'Editando aplicación...' : 'Nueva aplicación clínica...'}
                  className="h-10 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3] flex-1 min-w-[180px]"
                  data-testid="input-new-app-clinica"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveAppClinica();
                    }
                  }}
                />
                {editingAppClinica && (
                  <label className={`flex items-center gap-2 px-2.5 h-10 rounded-md border ${appClinicaForm.activo ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-100 border-slate-300'}`}>
                    <Switch
                      checked={appClinicaForm.activo}
                      onCheckedChange={(checked) => setAppClinicaForm({ ...appClinicaForm, activo: checked })}
                      className="data-[state=checked]:bg-emerald-600"
                      data-testid="app-clinica-activo-toggle"
                    />
                    <span className="text-xs font-medium">{appClinicaForm.activo ? 'Activa' : 'Inactiva'}</span>
                  </label>
                )}
                {editingAppClinica ? (
                  <div className="flex gap-1">
                    <Button
                      onClick={handleSaveAppClinica}
                      disabled={isSaving}
                      size="sm"
                      className="h-10 bg-[#505BB3] hover:bg-[#404A9C] text-white"
                      data-testid="btn-save-edit-app-clinica"
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10"
                      onClick={() => {
                        setEditingAppClinica(null);
                        setAppClinicaForm({ nombre: '', activo: true });
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={handleSaveAppClinica}
                    disabled={isSaving || !appClinicaForm.nombre.trim()}
                    className="h-10 bg-[#505BB3] hover:bg-[#404A9C] text-white"
                    data-testid="btn-add-app-clinica"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  </Button>
                )}
              </div>
            )}

            {/* List */}
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-80 overflow-y-auto">
              {(() => {
                const visibleApps = aplicacionesClinicas.filter(a => {
                  if (appsFilter === 'activos') return a.activo !== false;
                  if (appsFilter === 'inactivos') return a.activo === false;
                  return true;
                });
                if (visibleApps.length === 0) {
                  return (
                    <div className="p-6 text-center">
                      <Tag className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">
                        {appsFilter === 'inactivos' ? 'No hay aplicaciones inactivas' : 'No hay aplicaciones clínicas'}
                      </p>
                    </div>
                  );
                }
                return visibleApps.map(app => {
                  const usageCount = pruebas.filter(p => p.aplicaciones_clinicas?.includes(app.id)).length;
                  const inactive = app.activo === false;
                  return (
                    <div key={app.id} className={`flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 ${inactive ? 'opacity-60' : ''}`} data-testid={`app-clinica-item-${app.id}`}>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className={`bg-indigo-50 text-indigo-700 border-indigo-200 ${inactive ? 'line-through' : ''}`}>
                          {app.nombre}
                        </Badge>
                        {inactive && (
                          <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-medium uppercase tracking-wide">
                            Inactiva
                          </span>
                        )}
                        <span className="text-xs text-slate-400">{usageCount} prueba{usageCount !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex gap-1">
                        {canEdit() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 hover:bg-slate-200"
                            onClick={() => {
                              setEditingAppClinica(app);
                              setAppClinicaForm({ nombre: app.nombre, activo: app.activo !== false });
                            }}
                            data-testid={`btn-edit-app-${app.id}`}
                            title="Editar"
                          >
                            <Edit className="w-3.5 h-3.5 text-slate-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAppClinicaManager(false)}
              className="border-slate-300"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Especies Manager Dialog */}
      <Dialog
        open={showEspecieManager}
        onOpenChange={(open) => {
          if (!open) {
            setEditingEspecie(null);
            setEspecieForm({ nombre: '', activo: true });
          }
          setShowEspecieManager(open);
        }}
      >
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="especie-manager-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-emerald-600" />
              Especies
              <span className="text-sm font-normal text-slate-400 ml-auto">
                {especies.filter(e => e.activo !== false).length} activas
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-slate-500 mb-4">
              Las especies se asignan a cada prueba. Las especies protegidas (Canino, Felino, Otra especie) no pueden eliminarse.
            </p>

            {/* Filter dropdown */}
            <div className="mb-3">
              <Select value={especiesFilter} onValueChange={setEspeciesFilter}>
                <SelectTrigger className="h-8 text-xs bg-white" data-testid="especies-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activos"><span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Solo activas</span></SelectItem>
                  <SelectItem value="inactivos"><span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-slate-400" />Solo inactivas</span></SelectItem>
                  <SelectItem value="todos"><span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-violet-500" />Todas</span></SelectItem>
                </SelectContent>
              </Select>
            </div>

            {canCreate() && (
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Input
                  value={especieForm.nombre}
                  onChange={(e) => setEspecieForm({ ...especieForm, nombre: e.target.value })}
                  placeholder={editingEspecie ? 'Editando especie...' : 'Nueva especie...'}
                  className="h-10 border-slate-300 focus:border-emerald-500 focus:ring-emerald-500 flex-1 min-w-[180px]"
                  data-testid="input-new-especie"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveEspecie();
                    }
                  }}
                />
                {editingEspecie && (
                  <label className={`flex items-center gap-2 px-2.5 h-10 rounded-md border ${especieForm.activo ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-100 border-slate-300'}`}>
                    <Switch
                      checked={especieForm.activo}
                      onCheckedChange={(checked) => setEspecieForm({ ...especieForm, activo: checked })}
                      className="data-[state=checked]:bg-emerald-600"
                      data-testid="especie-activo-toggle"
                    />
                    <span className="text-xs font-medium">{especieForm.activo ? 'Activa' : 'Inactiva'}</span>
                  </label>
                )}
                {editingEspecie ? (
                  <div className="flex gap-1">
                    <Button
                      onClick={handleSaveEspecie}
                      disabled={isSaving}
                      size="sm"
                      className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="btn-save-edit-especie"
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10"
                      onClick={() => {
                        setEditingEspecie(null);
                        setEspecieForm({ nombre: '', activo: true });
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={handleSaveEspecie}
                    disabled={isSaving || !especieForm.nombre.trim()}
                    className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="btn-add-especie"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  </Button>
                )}
              </div>
            )}

            {/* List */}
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-80 overflow-y-auto">
              {(() => {
                const visibleEspecies = especies.filter(e => {
                  if (especiesFilter === 'activos') return e.activo !== false;
                  if (especiesFilter === 'inactivos') return e.activo === false;
                  return true;
                });
                if (visibleEspecies.length === 0) {
                  return (
                    <div className="p-6 text-center">
                      <FlaskConical className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">
                        {especiesFilter === 'inactivos' ? 'No hay especies inactivas' : 'No hay especies'}
                      </p>
                    </div>
                  );
                }
                return visibleEspecies.map(esp => {
                  const usageCount = pruebas.filter(p => p.especies?.includes(esp.id)).length;
                  const inactive = esp.activo === false;
                  return (
                    <div key={esp.id} className={`flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 ${inactive ? 'opacity-60' : ''}`} data-testid={`especie-item-${esp.id}`}>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className={`bg-emerald-50 text-emerald-700 border-emerald-200 ${inactive ? 'line-through' : ''}`}>
                          {esp.nombre}
                        </Badge>
                        {esp.protegida && (
                          <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">protegida</span>
                        )}
                        {inactive && (
                          <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-medium uppercase tracking-wide">
                            Inactiva
                          </span>
                        )}
                        <span className="text-xs text-slate-400">{usageCount} prueba{usageCount !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex gap-1">
                        {canEdit() && !esp.protegida && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 hover:bg-slate-200"
                            onClick={() => {
                              setEditingEspecie(esp);
                              setEspecieForm({ nombre: esp.nombre, activo: esp.activo !== false });
                            }}
                            data-testid={`btn-edit-esp-${esp.id}`}
                            title="Editar"
                          >
                            <Edit className="w-3.5 h-3.5 text-slate-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEspecieManager(false)}
              className="border-slate-300"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Áreas Diagnósticas Manager Dialog */}
      <Dialog
        open={showAreaManager}
        onOpenChange={(open) => {
          if (!open) {
            setEditingArea(null);
            setAreaForm({ nombre: '', orden: 0 });
          }
          setShowAreaManager(open);
        }}
      >
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="area-manager-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Folder className="w-5 h-5 text-violet-600" />
              Áreas Diagnósticas
              <span className="text-sm font-normal text-slate-400 ml-auto">{areasDiagnosticas.length} elementos</span>
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-slate-500 mb-4">
              Las áreas diagnósticas agrupan los grupos de pruebas (ej: Pruebas moleculares, Pruebas genéticas).
            </p>

            {canCreate() && (
              <div className="flex gap-2 mb-4">
                <Input
                  value={areaForm.nombre}
                  onChange={(e) => setAreaForm({ ...areaForm, nombre: e.target.value })}
                  placeholder="Nueva área diagnóstica..."
                  className="h-10 border-slate-300 focus:border-violet-500 focus:ring-violet-500"
                  data-testid="input-new-area"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (!editingArea) handleSaveArea();
                    }
                  }}
                />
                {editingArea ? (
                  <div className="flex gap-1">
                    <Button
                      onClick={handleSaveArea}
                      disabled={isSaving}
                      size="sm"
                      className="h-10 bg-violet-600 hover:bg-violet-700 text-white"
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10"
                      onClick={() => { setEditingArea(null); setAreaForm({ nombre: '', orden: 0 }); }}
                    >
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={handleSaveArea}
                    disabled={isSaving || !areaForm.nombre.trim()}
                    className="h-10 bg-violet-600 hover:bg-violet-700 text-white"
                    data-testid="btn-add-area"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  </Button>
                )}
              </div>
            )}

            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-80 overflow-y-auto">
              {areasDiagnosticas.length === 0 ? (
                <div className="p-6 text-center">
                  <Folder className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No hay áreas diagnósticas</p>
                </div>
              ) : (
                areasDiagnosticas.map(area => {
                  const gruposCount = grupos.filter(g => g.area_diagnostica_id === area.id && g.activo).length;
                  return (
                    <div key={area.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50" data-testid={`area-item-${area.id}`}>
                      <div className="flex items-center gap-3">
                        <Badge variant="secondary" className="bg-violet-50 text-violet-700 border-violet-200">
                          {area.nombre}
                        </Badge>
                        <span className="text-xs text-slate-400">{gruposCount} grupo{gruposCount !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex gap-1">
                        {canEdit() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 hover:bg-slate-200"
                            onClick={() => { setEditingArea(area); setAreaForm({ nombre: area.nombre, orden: area.orden }); }}
                          >
                            <Edit className="w-3.5 h-3.5 text-slate-500" />
                          </Button>
                        )}
                        {canDelete() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 hover:bg-red-100"
                            onClick={() => handleDeleteArea(area.id)}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAreaManager(false)} className="border-slate-300">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
